import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { Field } from './Field.jsx';
import { ProfileSection } from './ProfileSection.jsx';
import { inputStyle } from '../lib/ui.js';
import { WeeklyAvailabilityEditor } from './WeeklyAvailabilityEditor.jsx';

export function AvailabilitySection({ currentUser, targetUserId, canEdit, embedded = false }) {
  const [reductions, setReductions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);   // reduction object being edited
  const [overrideOpen, setOverrideOpen] = useState(null); // reduction id whose override modal is open
  const [overrideDate, setOverrideDate] = useState('');
  const [reasonForManager, setReasonForManager] = useState('');
  const headers = { 'x-user-id': String(currentUser.id) };
  const isSelf = currentUser.id === targetUserId;

  const blank = {
    employee_id: targetUserId,
    // Migration 076 added kind. Default to 'restriction' (the legacy
    // "list times you can't work" model). Toggle in the form lets a
    // staffer flip to 'preference_available' if they'd rather enter
    // what they CAN work (Square-style).
    kind: 'restriction',
    reduction_type: 'full_day',
    recurrence: 'weekly',
    days_of_week: ['mon'],
    one_time_date: '',
    start_time: '09:00',
    end_time: '17:00',
    effective_start_date: '',
    effective_end_date: '',
    note: '',
  };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true); setError('');
    try {
      const r = await api('GET', `/api/availability/reductions/${targetUserId}`, null, headers);
      setReductions(r.reductions || []);
    } catch (e) { setError(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  }
  // Your own profile renders the weekly grid, which loads its own data.
  useEffect(() => { if (!isSelf) load(); }, [targetUserId]);

  function startCreate() { setEditing(null); setForm({ ...blank }); setReasonForManager(''); setShowForm(true); }
  function startEdit(r) {
    setEditing(r);
    setForm({
      employee_id: r.employee_id,
      kind: r.kind || 'restriction',
      reduction_type: r.reduction_type,
      recurrence: r.recurrence,
      days_of_week: (r.days && r.days.length) ? r.days : (r.day_of_week ? [r.day_of_week] : ['mon']),
      one_time_date: r.one_time_date || '',
      start_time: r.start_time || '09:00',
      end_time: r.end_time || '17:00',
      effective_start_date: r.effective_start_date || '',
      effective_end_date: r.effective_end_date || '',
      note: r.note || '',
    });
    setReasonForManager('');
    setShowForm(true);
  }

  async function save() {
    setSaving(true); setError('');
    try {
      const payload = { ...form };
      if (!isSelf && reasonForManager) payload.reason = reasonForManager;
      const r = editing
        ? await api('PUT', `/api/availability/reductions/${editing.id}`, payload, headers)
        : await api('POST', '/api/availability/reductions', payload, headers);
      setShowForm(false); setEditing(null); load();
      // CJ 2026-06-15: server surfaces opposite-kind overlaps in the
      // response. Pop a friendly note — not a hard block — so the
      // staffer knows the net effect ("you're available 9-5 AND
      // unavailable 1-3 → effective 9-1 + 3-5").
      const conflicts = Array.isArray(r?.conflicts) ? r.conflicts : [];
      if (conflicts.length > 0) {
        const dayLabels = { mon:'Mon', tue:'Tue', wed:'Wed', thu:'Thu', fri:'Fri', sat:'Sat', sun:'Sun' };
        const lines = conflicts.map(c => {
          const ds = (c.days || []).map(d => dayLabels[d] || d).join(', ');
          const verb = c.kind === 'preference_available' ? 'Available' : 'Off';
          const when = c.recurrence === 'one_time' ? c.one_time_date : ds;
          const range = c.reduction_type === 'time_range'
            ? ` ${c.start_time}–${c.end_time}`
            : ' (whole day)';
          return `• ${verb} ${when}${range}`;
        });
        alert(`Saved. Heads up — this overlaps an existing entry of the opposite kind:\n\n${lines.join('\n')}\n\nThe schedule will reconcile them (an NA window inside an "available" window just trims it). You can leave both as-is or open the editor to fix.`);
      }
    } catch (e) {
      // Surface validation errors when the server provides a details array.
      let msg = e.message || 'Save failed';
      try { const parsed = JSON.parse(msg); if (parsed.details) msg = parsed.details.join(' · '); } catch {}
      setError(msg);
    }
    finally { setSaving(false); }
  }

  async function remove(r) {
    if (!confirm(`Delete this availability change? This will also remove any one-off overrides on it.`)) return;
    try {
      await api('DELETE', `/api/availability/reductions/${r.id}`,
        !isSelf && reasonForManager ? { reason: reasonForManager } : null, headers);
      load();
    } catch (e) { setError(e.message || 'Delete failed'); }
  }

  async function suppressOne(reductionId) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(overrideDate)) { setError('Pick a date'); return; }
    try {
      await api('POST', `/api/availability/reductions/${reductionId}/override`,
        { override_date: overrideDate }, headers);
      setOverrideOpen(null); setOverrideDate(''); load();
    } catch (e) { setError(e.message || 'Override failed'); }
  }

  async function clearSuppression(reductionId, overrideId) {
    try {
      await api('DELETE', `/api/availability/reductions/${reductionId}/override/${overrideId}`, null, headers);
      load();
    } catch (e) { setError(e.message || 'Clear failed'); }
  }

  function describeReduction(r) {
    const dayLabels = { mon:'Mon', tue:'Tue', wed:'Wed', thu:'Thu', fri:'Fri', sat:'Sat', sun:'Sun' };
    const days = (r.days && r.days.length) ? r.days : (r.day_of_week ? [r.day_of_week] : []);
    const dayList = days.map((d) => dayLabels[d] || d).join(', ');
    const when =
      r.recurrence === 'one_time' ? r.one_time_date :
      r.recurrence === 'biweekly' ? `every other ${dayList}` :
                                     `every ${dayList}`;
    // Migration 076: positive availability windows. The verb flips —
    // "Off Mon" vs "Available Mon" — but the date/time half stays the
    // same so the row reads naturally either way.
    const isPositive = (r.kind || 'restriction') === 'preference_available';
    if (r.reduction_type === 'full_day') return `${isPositive ? 'Available' : 'Off'} ${when}`;
    return `${isPositive ? 'Available' : 'Off'} ${when}, ${r.start_time}–${r.end_time}`;
  }

  // Staff set their own availability with the weekly grid: one answer per day,
  // no "can't" vs "CAN" choice (docs/AVAILABILITY-WEEKLY-GRID-SPEC.md). The row
  // list and form below are the manager's view of someone else.
  if (isSelf) return <WeeklyAvailabilityEditor currentUser={currentUser} targetUserId={targetUserId} />;

  // When embedded inside another card (e.g. the merged Schedule
  // preferences section in EmployeeProfileView), skip our own
  // ProfileSection wrapper so the visual nesting reads as one card.
  // CJ feedback 2026-06-15: half the staff entered "times I can work"
  // and half entered "times I can't work" because the labels were
  // ambiguous. The data model has always been reductions (i.e. times
  // you CAN'T work — anything not listed is assumed available). Make
  // that explicit everywhere staff sees it.
  const Wrapper = embedded
    ? ({ children }) => (
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#212121',
                          textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
            When you're not available
          </div>
          {children}
        </div>
      )
    : ({ children }) => (
        <ProfileSection title="When you're not available" canEdit={false}
          editing={false}
          onEdit={() => {}} onCancel={() => {}} onSave={() => {}}>
          {children}
        </ProfileSection>
      );
  return (
    <Wrapper>
      <div style={{ fontSize: 11, color: '#757575', marginBottom: 6, lineHeight: 1.45 }}>
        Restrictions you add for this employee will be logged in the override audit.
      </div>
      {error && <div style={{ fontSize: 11, color: '#c62828', marginBottom: 6 }}>{error}</div>}

      {loading ? (
        <div style={{ fontSize: 12, color: '#757575' }}>Loading…</div>
      ) : (
        <>
          {reductions.length === 0 && (
            <div style={{ fontSize: 12, color: '#9e9e9e', fontStyle: 'italic', padding: '6px 0' }}>
              No restrictions — fully available within your location's operating hours.
            </div>
          )}
          {reductions.map((r) => {
            // Status badge (CJ 2026-06-15): show the staffer + manager
            // where each row is in the acknowledgement workflow.
            const statusBadge = (() => {
              if (!r.status || r.status === 'active') return null;
              const stylesByStatus = {
                pending_ack:      { label: 'Awaiting acknowledgement', bg: '#fff7ed', fg: '#9a3412', border: '#fed7aa' },
                pending_approval: { label: 'Awaiting approval',        bg: '#fef3c7', fg: '#92400e', border: '#fcd34d' },
                rejected:         { label: 'Rejected',                 bg: '#fef2f2', fg: '#991b1b', border: '#fecaca' },
              };
              const s = stylesByStatus[r.status];
              if (!s) return null;
              return (
                <span style={{ fontSize: 10, padding: '1px 8px', borderRadius: 99,
                                background: s.bg, color: s.fg, border: `1px solid ${s.border}`,
                                fontWeight: 600, marginLeft: 6, verticalAlign: 'middle' }}>
                  {s.label}
                </span>
              );
            })();
          return (
            <div key={r.id} style={{ borderBottom: '1px solid #f0f0f0', padding: '8px 0', display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap',
                                       opacity: (r.status && r.status !== 'active') ? 0.85 : 1 }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontSize: 13, fontWeight: 600,
                                // CJ 2026-06-15: green for positive availability
                                // entries, default ink for restrictions — keeps
                                // the two kinds visually distinct at a glance.
                                color: (r.kind || 'restriction') === 'preference_available' ? '#166534' : '#212121' }}>
                  {describeReduction(r)}
                  <span style={{ fontSize: 10, color: '#9e9e9e', fontWeight: 500, marginLeft: 6 }}>
                    {r.recurrence === 'one_time' ? '· one-time' : `· ${r.recurrence}`}
                  </span>
                  {statusBadge}
                </div>
                {r.status === 'rejected' && r.decision_note && (
                  <div style={{ fontSize: 11, color: '#991b1b', marginTop: 2 }}>"{r.decision_note}"</div>
                )}
                {(r.effective_start_date || r.effective_end_date) && (
                  <div style={{ fontSize: 11, color: '#757575', marginTop: 2 }}>
                    {r.effective_start_date || '—'} → {r.effective_end_date || 'ongoing'}
                  </div>
                )}
                {r.note && <div style={{ fontSize: 11, color: '#757575', marginTop: 2 }}>"{r.note}"</div>}
                {r.overrides && r.overrides.length > 0 && (
                  <div style={{ fontSize: 11, color: '#1565c0', marginTop: 4 }}>
                    Skipped on:{' '}
                    {r.overrides.map((o) => (
                      <span key={o.id} style={{ marginRight: 6 }}>
                        {o.override_date}
                        {canEdit && (
                          <button onClick={() => clearSuppression(r.id, o.id)}
                            style={{ marginLeft: 3, fontSize: 10, color: '#c62828', background: 'transparent', border: 'none', cursor: 'pointer' }}>
                            ✕
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              {canEdit && (
                <div style={{ display: 'flex', gap: 4 }}>
                  {r.recurrence !== 'one_time' && (
                    <button onClick={() => { setOverrideOpen(r.id); setOverrideDate(''); }}
                      className="btn-secondary" style={{ fontSize: 11, padding: '3px 8px' }}>
                      Skip date
                    </button>
                  )}
                  <button onClick={() => startEdit(r)}
                    className="btn-secondary" style={{ fontSize: 11, padding: '3px 8px' }}>Edit</button>
                  <button onClick={() => remove(r)}
                    style={{ fontSize: 11, padding: '3px 8px', background: '#fff', color: '#c62828', border: '1px solid #ffcdd2', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit' }}>
                    Delete
                  </button>
                </div>
              )}
              {overrideOpen === r.id && (
                <div style={{ flexBasis: '100%', marginTop: 6, padding: 8, background: '#fafbfc', border: '1px solid #d0d4d9', borderRadius: 6, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, color: '#37474f' }}>Skip this rule on date:</span>
                  <input type="date" value={overrideDate}
                    onChange={(e) => setOverrideDate(e.target.value)}
                    style={{ padding: '3px 6px', fontSize: 12, border: '1px solid #d0d4d9', borderRadius: 4 }} />
                  <button onClick={() => suppressOne(r.id)}
                    style={{ fontSize: 11, padding: '4px 10px', background: 'var(--forest)', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>
                    Save skip
                  </button>
                  <button onClick={() => setOverrideOpen(null)} className="btn-secondary" style={{ fontSize: 11, padding: '3px 8px' }}>Cancel</button>
                </div>
              )}
            </div>
          );
          })}
        </>
      )}

      {canEdit && !showForm && (
        <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => { setForm({ ...blank, kind: 'restriction' }); setEditing(null); setReasonForManager(''); setShowForm(true); }}
            className="btn-secondary" style={{ fontSize: 12 }}>+ Add a time you can't work</button>
          <button onClick={() => { setForm({ ...blank, kind: 'preference_available' }); setEditing(null); setReasonForManager(''); setShowForm(true); }}
            style={{ fontSize: 12, padding: '6px 12px', background: '#dcfce7', color: '#166534',
                      border: '1px solid #86efac', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>
            + Add a time you CAN work
          </button>
        </div>
      )}

      {showForm && (
        <div style={{ marginTop: 12, padding: 12, border: '1px solid #d0d4d9', borderRadius: 8, background: '#fafbfc' }}>
          {/* Migration 076: the form supports both kinds. Mode toggle
              at the top flips labels + help copy so the staffer sees
              the verb appropriate to what they're entering. */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 8, border: '1px solid #d0d4d9', borderRadius: 6, padding: 2, width: 'fit-content' }}>
            {[
              { id: 'restriction', label: "Times I can't work", bg: '#fff' },
              { id: 'preference_available', label: 'Times I CAN work', bg: '#dcfce7' },
            ].map(opt => (
              <button key={opt.id} type="button"
                onClick={() => setForm(f => ({ ...f, kind: opt.id }))}
                style={{ padding: '4px 12px', fontSize: 11, fontWeight: 600,
                          background: form.kind === opt.id ? (opt.id === 'preference_available' ? '#166534' : 'var(--forest)') : 'transparent',
                          color: form.kind === opt.id ? '#fff' : '#37474f',
                          border: 'none', borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit' }}>
                {opt.label}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
            {(() => {
              const isPositive = form.kind === 'preference_available';
              if (editing) return isPositive ? 'Edit a time you CAN work' : "Edit a time you can't work";
              return isPositive ? 'Mark a time you CAN work' : "Mark a time you can't work";
            })()}
          </div>
          <div style={{ fontSize: 11, color: '#757575', marginBottom: 8 }}>
            {form.kind === 'preference_available'
              ? 'The schedule will treat times outside this window as unavailable. Use this when you\'d rather list when you CAN work.'
              : 'This blocks the schedule from putting you on. Anything you don\'t list stays available.'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
            <Field label="What you're blocking">
              <select value={form.reduction_type}
                onChange={(e) => setForm((f) => ({ ...f, reduction_type: e.target.value }))}
                style={inputStyle}>
                <option value="full_day">A whole day</option>
                <option value="time_range">Part of a day (time range)</option>
              </select>
            </Field>
            <Field label="Recurrence">
              <select value={form.recurrence}
                onChange={(e) => setForm((f) => ({ ...f, recurrence: e.target.value }))}
                style={inputStyle}>
                <option value="weekly">Every week</option>
                <option value="biweekly">Every other week</option>
                <option value="one_time">One-time</option>
              </select>
            </Field>
            {form.recurrence !== 'one_time' && (
              <Field label="Days of week (pick one or more)">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {[['mon','M'],['tue','T'],['wed','W'],['thu','T'],['fri','F'],['sat','S'],['sun','S']].map(([v, l]) => {
                    const checked = (form.days_of_week || []).includes(v);
                    return (
                      <label key={v} title={({mon:'Monday',tue:'Tuesday',wed:'Wednesday',thu:'Thursday',fri:'Friday',sat:'Saturday',sun:'Sunday'})[v]}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          width: 38, height: 38, borderRadius: 8,
                          border: `1.5px solid ${checked ? 'var(--forest)' : '#d0d4d9'}`,
                          background: checked ? 'rgba(45,122,58,0.12)' : '#fff',
                          color: checked ? 'var(--forest)' : '#37474f',
                          fontWeight: checked ? 700 : 500, fontSize: 13, cursor: 'pointer',
                          userSelect: 'none', transition: 'all 0.12s',
                        }}>
                        <input type="checkbox" checked={checked}
                          onChange={(e) => setForm((f) => ({
                            ...f,
                            days_of_week: e.target.checked
                              ? [...(f.days_of_week || []), v]
                              : (f.days_of_week || []).filter((x) => x !== v),
                          }))}
                          style={{ display: 'none' }} />
                        {l}
                      </label>
                    );
                  })}
                </div>
              </Field>
            )}
            {form.recurrence === 'one_time' && (
              <Field label="Date">
                <input type="date" value={form.one_time_date}
                  onChange={(e) => setForm((f) => ({ ...f, one_time_date: e.target.value }))}
                  style={inputStyle} />
              </Field>
            )}
            {form.reduction_type === 'time_range' && (
              <>
                <Field label="From (30-min)">
                  <input type="time" step="1800" value={form.start_time}
                    onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))}
                    style={inputStyle} />
                </Field>
                <Field label="To (30-min)">
                  <input type="time" step="1800" value={form.end_time}
                    onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))}
                    style={inputStyle} />
                </Field>
              </>
            )}
            {form.recurrence !== 'one_time' && (
              <>
                <Field label="Effective from (optional)">
                  <input type="date" value={form.effective_start_date}
                    onChange={(e) => setForm((f) => ({ ...f, effective_start_date: e.target.value }))}
                    style={inputStyle} />
                </Field>
                <Field label="Effective until (optional)">
                  <input type="date" value={form.effective_end_date}
                    onChange={(e) => setForm((f) => ({ ...f, effective_end_date: e.target.value }))}
                    style={inputStyle} />
                </Field>
              </>
            )}
            <Field label="Note (optional)">
              <input value={form.note}
                onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                placeholder="e.g. school, second job"
                style={inputStyle} />
            </Field>
            {!isSelf && (
              <Field label="Reason (logged)">
                <input value={reasonForManager}
                  onChange={(e) => setReasonForManager(e.target.value)}
                  placeholder="why this manual edit"
                  style={inputStyle} />
              </Field>
            )}
          </div>
          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
            <button onClick={save} disabled={saving}
              style={{ background: 'var(--forest)', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.5 : 1 }}>
              {saving ? 'Saving…' : (editing ? 'Save changes' : 'Add availability change')}
            </button>
            <button onClick={() => { setShowForm(false); setEditing(null); }}
              className="btn-secondary" style={{ fontSize: 12 }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </Wrapper>
  );
}
